import type { EmsComponents, NormalizedNullableResponse } from '../../api/ems';
import { normalizePortalCall } from './call';
import type { PortalCall } from './call';
import { normalizePortalLocation } from './location';
import type { PortalLocation } from './location';
import { normalizeNullable } from './normalizeNullable';

export type IncidentWire = EmsComponents['schemas']['PortalIncident'];
export type IncidentAgencyViewWire = IncidentWire['agencyViews'][number];

type NormalizedIncident = NormalizedNullableResponse<IncidentWire>;
type NormalizedIncidentAgencyView = NormalizedNullableResponse<IncidentAgencyViewWire>;
type CallerWire = IncidentWire['caller'];
type NormalizedCaller = NormalizedNullableResponse<CallerWire>;
type LiveVideoWire = CallerWire['liveVideos'][number];

export type LiveVideo = NormalizedNullableResponse<LiveVideoWire>;
export type Caller = Omit<NormalizedCaller, 'liveVideos'> & { liveVideos: LiveVideo[] };
export type IncidentAgencyView = Omit<NormalizedIncidentAgencyView, 'calls'> & { calls: PortalCall[] };
export type Incident = Omit<
  NormalizedIncident,
  'caller' | 'agencyViews' | 'positions' | 'lastPositionBySource' | 'lastPosition'
> & {
  caller: Caller;
  agencyViews: IncidentAgencyView[];
  positions: PortalLocation[];
  lastPositionBySource: PortalLocation[];
  lastPosition: PortalLocation | null;
};

export type Dispatcher = Incident['dispatcher'];
export type Attachment = Incident['attachments'][number];
export type AdditionalIncidentData = Incident['additionalIncidentData'];
export type IncidentAgency = Incident['involvedAgencies'][number];
export type CollaborationStateHistoryRecord = IncidentAgency['statusHistory'][number];

export type DeviceInfo = NonNullable<Caller['deviceInfo']>;
export type EmergencyContact = Caller['contacts'][number];
export type Classification = NonNullable<Caller['classification']>;
export type PermanentResidence = NonNullable<Caller['permanentResidence']>;
export type MedicData = NonNullable<Caller['medicData']>;
export type UserInfo = LiveVideo['startedByUser'];
export type GeoPoint = NonNullable<IncidentAgency['position']>;

export type IncidentState = Incident['state'];
export type CollaborationState = CollaborationStateHistoryRecord['state'];
export type LiveVideoState = Incident['videoState'];
export type LiveVideoSource = LiveVideo['source'];
export type DeviceOs = DeviceInfo['system'];
export type InternetConnectionType = DeviceInfo['internetConnectionType'];
export type Sex = MedicData['person']['sex'];
export type BloodType = MedicData['person']['bloodType'];

export type AppleEedLiveVideo = Extract<LiveVideo, { source: 'AppleEed' }>;
export type WebAppLiveVideo = Extract<LiveVideo, { source: 'WebApp' }>;
export type GoogleElsLiveVideo = Extract<LiveVideo, { source: 'GoogleEls' }>;

export function normalizeIncidentAgencyView(view: IncidentAgencyViewWire): IncidentAgencyView {
  return {
    ...view,
    calls: view.calls.map(normalizePortalCall),
  };
}

function normalizeLiveVideo(video: LiveVideoWire): LiveVideo {
  return {
    ...video,
    endTimeUtc: video.endTimeUtc ?? null,
  };
}

function normalizeMedicData(medicData: CallerWire['medicData']): Caller['medicData'] {
  if (medicData == null) {
    return null;
  }

  return {
    ...medicData,
    person: {
      ...medicData.person,
      yearOfBirth: medicData.person.yearOfBirth ?? null,
      height: medicData.person.height ?? null,
      weight: medicData.person.weight ?? null,
    },
    conditions: {
      ...medicData.conditions,
      pregnancy: {
        ...medicData.conditions.pregnancy,
        description: medicData.conditions.pregnancy.description ?? null,
      },
      description: medicData.conditions.description ?? null,
    },
    assistance: {
      ...medicData.assistance,
      description: medicData.assistance.description ?? null,
    },
    organDonation:
      medicData.organDonation == null
        ? null
        : {
            ...medicData.organDonation,
            description: medicData.organDonation.description ?? null,
          },
    medications: medicData.medications ?? null,
    allergies: medicData.allergies ?? null,
    advanceDirectives: medicData.advanceDirectives ?? null,
    notes: {
      caller: medicData.notes.caller ?? null,
      nextOfKin: medicData.notes.nextOfKin ?? null,
    },
  };
}

function normalizeDeviceInfo(deviceInfo: CallerWire['deviceInfo']): Caller['deviceInfo'] {
  if (deviceInfo == null) {
    return null;
  }

  return {
    ...deviceInfo,
    batteryLevel: deviceInfo.batteryLevel ?? null,
    systemVersion: deviceInfo.systemVersion ?? null,
  };
}

function normalizePermanentResidence(
  residence: CallerWire['permanentResidence'],
): Caller['permanentResidence'] {
  if (residence == null) {
    return null;
  }

  return {
    ...residence,
    hint: residence.hint ?? null,
    coordinates: residence.coordinates ?? null,
  };
}

function normalizeDataSources(dataSources: CallerWire['dataSources']): Caller['dataSources'] {
  return {
    mobileApp: dataSources.mobileApp.map((source) => ({
      ...source,
      appInfo: {
        ...source.appInfo,
        language: source.appInfo.language ?? null,
      },
      data: normalizeNullable(source.data ?? null),
    })),
    mobileAppSms: dataSources.mobileAppSms,
    webApp: dataSources.webApp.map((source) => ({
      ...source,
      deviceInfo:
        source.deviceInfo == null
          ? null
          : {
              ...source.deviceInfo,
              batteryLevel: source.deviceInfo.batteryLevel ?? null,
              systemVersion: source.deviceInfo.systemVersion ?? null,
              userAgent: source.deviceInfo.userAgent ?? null,
            },
    })),
    appleEed: dataSources.appleEed,
    googleEls: dataSources.googleEls.map((source) => ({
      ...source,
      emergencyType: source.emergencyType ?? null,
      data: normalizeNullable(source.data ?? null),
    })),
    aml: dataSources.aml,
  };
}

function normalizeCaller(caller: CallerWire): Caller {
  return {
    ...caller,
    deviceInfo: normalizeDeviceInfo(caller.deviceInfo),
    liveVideos: caller.liveVideos.map(normalizeLiveVideo),
    fullname: caller.fullname ?? null,
    email: caller.email ?? null,
    insuranceNumber: caller.insuranceNumber ?? null,
    medicData: normalizeMedicData(caller.medicData),
    permanentResidence: normalizePermanentResidence(caller.permanentResidence),
    contacts: caller.contacts.map((contact) => ({
      ...contact,
      email: contact.email ?? null,
      relationship: contact.relationship ?? null,
    })),
    classification: caller.classification ?? null,
    dataSources: normalizeDataSources(caller.dataSources),
  };
}

export function normalizeIncident(incident: IncidentWire): Incident {
  const locationWire = incident as unknown as IncidentWire & {
    positions: PortalLocation[];
    lastPositionBySource: PortalLocation[];
    lastPosition?: PortalLocation | null;
  };

  return {
    ...incident,
    caller: normalizeCaller(incident.caller),
    dispatcher: {
      ...incident.dispatcher,
      preferredLocationSource: incident.dispatcher.preferredLocationSource ?? null,
    },
    patient: {
      ...incident.patient,
      description: incident.patient.description ?? null,
    },
    appleEedAdditionalDataExpiresAtUtc: incident.appleEedAdditionalDataExpiresAtUtc ?? null,
    chatId: incident.chatId ?? null,
    chatMessages:
      incident.chatMessages == null
        ? null
        : incident.chatMessages.map((message) => ({
            ...message,
            message: message.message ?? null,
            attachedImageUrl: message.attachedImageUrl ?? null,
          })),
    agencyViews: incident.agencyViews.map(normalizeIncidentAgencyView),
    liveState: {
      ...incident.liveState,
      callerMessagePreview: incident.liveState.callerMessagePreview ?? null,
    },
    positions: locationWire.positions.map(normalizePortalLocation),
    lastPositionBySource: locationWire.lastPositionBySource.map(normalizePortalLocation),
    activeLiveVideoId: incident.activeLiveVideoId ?? null,
    closedTimeUtc: incident.closedTimeUtc ?? null,
    lastPosition:
      locationWire.lastPosition == null ? null : normalizePortalLocation(locationWire.lastPosition),
    involvedAgencies: incident.involvedAgencies.map((agency) => ({
      ...agency,
      position: agency.position ?? null,
      requestedBy: agency.requestedBy ?? null,
    })),
    additionalIncidentData: {
      ...incident.additionalIncidentData,
      incidentLabel: incident.additionalIncidentData.incidentLabel ?? null,
      callSign: incident.additionalIncidentData.callSign ?? null,
      ateMessage: incident.additionalIncidentData.ateMessage ?? null,
    },
  };
}

type ExactlyTheUnion<TUnion extends string> = { [TMember in TUnion]: TMember };

export const IncidentState = {
  Open: 'Open',
  Closed: 'Closed',
} as const satisfies ExactlyTheUnion<IncidentState>;

export const CollaborationState = {
  Activated: 'Activated',
  Invited: 'Invited',
  Accepted: 'Accepted',
  Declined: 'Declined',
  Supervising: 'Supervising',
} as const satisfies ExactlyTheUnion<CollaborationState>;

export const LiveVideoState = {
  Inactive: 'Inactive',
  Active: 'Active',
  Ended: 'Ended',
} as const satisfies ExactlyTheUnion<LiveVideoState>;

export const LiveVideoSource = {
  WebApp: 'WebApp',
  AppleEed: 'AppleEed',
  GoogleEls: 'GoogleEls',
} as const satisfies ExactlyTheUnion<LiveVideoSource>;

export const DeviceOs = {
  Unknown: 'Unknown',
  iOS: 'iOS',
  Android: 'Android',
  WindowsPhone: 'WindowsPhone',
  Aligator: 'Aligator',
} as const satisfies ExactlyTheUnion<DeviceOs>;

export const InternetConnectionType = {
  Unknown: 'Unknown',
  NoConnection: 'NoConnection',
  WiFi: 'WiFi',
  Cellular2G: 'Cellular2G',
  Cellular3G: 'Cellular3G',
  Cellular4G: 'Cellular4G',
  Cellular5G: 'Cellular5G',
} as const satisfies ExactlyTheUnion<InternetConnectionType>;

const internetConnectionLabels = {
  Unknown: 'Unknown',
  NoConnection: 'NoConnection',
  WiFi: 'WiFi',
  Cellular2G: 'G2',
  Cellular3G: 'G3',
  Cellular4G: 'G4',
  Cellular5G: 'G5',
} as const satisfies Record<InternetConnectionType, string>;

export function internetConnectionLabel(connectionType: InternetConnectionType): string {
  return internetConnectionLabels[connectionType];
}

export const Sex = {
  Unknown: 'Unknown',
  Male: 'Male',
  Female: 'Female',
  Other: 'Other',
  Intersex: 'Intersex',
} as const satisfies ExactlyTheUnion<Sex>;

export const BloodType = {
  Unknown: 'Unknown',
  ZeroNegative: 'ZeroNegative',
  ZeroPositive: 'ZeroPositive',
  ANegative: 'ANegative',
  APositive: 'APositive',
  BNegative: 'BNegative',
  BPositive: 'BPositive',
  ABNegative: 'ABNegative',
  ABPositive: 'ABPositive',
  ZeroUnknown: 'ZeroUnknown',
  AUnknown: 'AUnknown',
  BUnknown: 'BUnknown',
  ABUnknown: 'ABUnknown',
  HH: 'HH',
} as const satisfies ExactlyTheUnion<BloodType>;
